import { AwaitContainer, Button, Combobox, ComboboxItem, Switch, Textarea } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { hasOwnAttributeValue } from '@/entities/attributes';
import { useConversationsAttributes } from '@/entities/conversations';
import type { BitrixTouchSettings } from '@/entities/integrations';
import { useBitrixTouchSettings, useSaveBitrixTouchSettings } from '@/entities/integrations';
import type { PredicateValue } from '@/entities/predicate';
import { hasPredicateIssues } from '@/entities/predicate';
import { PredicateInput } from '@/features/PredicateInput';
import { TemplatePreviewDialog } from '@/features/TemplatePreviewDialog';
import { getApiErrorMessage } from '@/shared/api';
import { toastError, toastSuccess } from '@/shared/lib/utils/toast';

type Props = {
  /** Доступна ли правка настроек (право управления интеграцией) */
  canEdit: boolean;
};

/** Значения формы настроек режима касания */
type BitrixTouchFormValues = {
  isEnabled: boolean;
  summaryTemplate: string;
  predicate: PredicateValue;
  companyAttributePath: string;
  contactAttributePath: string;
  dateTimeAttributePath: string;
  assignedByAttributePath: string;
};

const toFormValues = (settings?: BitrixTouchSettings): BitrixTouchFormValues => ({
  isEnabled: settings?.isEnabled ?? false,
  summaryTemplate: settings?.summaryTemplate ?? '',
  predicate: { dsl: settings?.predicate?.dsl ?? '', dslHuman: settings?.predicate?.dslHuman ?? '' },
  companyAttributePath: settings?.companyAttributePath ?? '',
  contactAttributePath: settings?.contactAttributePath ?? '',
  dateTimeAttributePath: settings?.dateTimeAttributePath ?? '',
  assignedByAttributePath: settings?.assignedByAttributePath ?? '',
});

/**
 * Варианты выбора вместе с уже сохранённым путём: сохранённое мимо справочника (снятый с учёта
 * атрибут, путь, заданный не отсюда) иначе пропало бы из поля — и сохранение стёрло бы его молча.
 */
const withSavedPath = (options: ComboboxItem[], path: string): ComboboxItem[] =>
  !path || options.some((option) => option.value === path) ? options : [...options, { value: path, label: path }];

/**
 * Настройки режима касания Bitrix24: включение режима, шаблон итога разговора с проверкой его
 * на записи, условие отбора диалогов и сопоставление атрибутов компании, контакта, даты-времени
 * и ответственного сотрудника.
 *
 * Непригодное условие сохранять не даёт сама форма; незаполненные настройки включаемого режима
 * отклоняет сервер — его причину пользователь и видит.
 */
export const BitrixTouchSettingsForm: FC<Props> = ({ canEdit }) => {
  const { t } = useTranslation(['integrations', 'common']);
  const { data: settings, isLoading } = useBitrixTouchSettings();
  const { mutateAsync: saveSettings, isPending } = useSaveBitrixTouchSettings();
  const { data: attributesData } = useConversationsAttributes({ 'is-deleted': false, limit: 1000 });

  const values = useMemo(() => toFormValues(settings), [settings]);
  const methods = useForm<BitrixTouchFormValues>({ values });
  const { control, handleSubmit, watch } = methods;

  // В сопоставление идут только атрибуты, у которых у диалога бывает собственное значение:
  // сопоставленный системному полю атрибут оставил бы поле касания пустым
  const attributeOptions = useMemo<ComboboxItem[]>(
    () =>
      (attributesData?.attributes ?? [])
        .filter(hasOwnAttributeValue)
        .map((attribute) => ({ value: attribute.path, label: attribute.pathDescription || attribute.name })),
    [attributesData],
  );

  const summaryTemplate = watch('summaryTemplate');

  const onSubmit = async (formValues: BitrixTouchFormValues) => {
    try {
      await saveSettings({
        isEnabled: formValues.isEnabled,
        summaryTemplate: formValues.summaryTemplate.trim() || null,
        // Условие уходит записью: пустая запись снимает условие
        predicate: formValues.predicate?.dsl
          ? { dsl: formValues.predicate.dsl, dslHuman: formValues.predicate.dslHuman }
          : null,
        companyAttributePath: formValues.companyAttributePath || null,
        contactAttributePath: formValues.contactAttributePath || null,
        dateTimeAttributePath: formValues.dateTimeAttributePath || null,
        assignedByAttributePath: formValues.assignedByAttributePath || null,
      });
      toastSuccess(t('integrations:bitrixTouch.saveSuccess'));
    } catch (error) {
      toastError(t('integrations:bitrixTouch.saveError'), { description: getApiErrorMessage(error) });
    }
  };

  return (
    <AwaitContainer isLoading={isLoading}>
      <FormProvider {...methods}>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          <Switch
            control={control}
            name="isEnabled"
            label={t('integrations:bitrixTouch.fields.isEnabled')}
            disabled={!canEdit}
          />
          <div className="flex flex-col gap-2">
            <Textarea
              control={control}
              name="summaryTemplate"
              label={t('integrations:bitrixTouch.fields.summaryTemplate')}
              description={t('integrations:bitrixTouch.fields.summaryTemplateDescription')}
              rows={4}
              disabled={!canEdit}
            />
            <TemplatePreviewDialog
              template={summaryTemplate}
              disabled={!summaryTemplate.trim()}
              trigger={{ className: 'self-start' }}
            />
          </div>
          <PredicateInput
            control={control}
            name="predicate"
            label={t('integrations:bitrixTouch.fields.predicate')}
            type="textarea"
            rows={1}
            disabled={!canEdit}
            rules={{
              validate: (value) => !hasPredicateIssues(value) || t('common:predicateHasIssues'),
            }}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Combobox
              control={control}
              name="companyAttributePath"
              label={t('integrations:bitrixTouch.fields.companyAttributePath')}
              options={withSavedPath(attributeOptions, values.companyAttributePath)}
              emptyMessage={t('common:emptyCombobox')}
              disabled={!canEdit}
            />
            <Combobox
              control={control}
              name="contactAttributePath"
              label={t('integrations:bitrixTouch.fields.contactAttributePath')}
              options={withSavedPath(attributeOptions, values.contactAttributePath)}
              emptyMessage={t('common:emptyCombobox')}
              disabled={!canEdit}
            />
            <Combobox
              control={control}
              name="dateTimeAttributePath"
              label={t('integrations:bitrixTouch.fields.dateTimeAttributePath')}
              options={withSavedPath(attributeOptions, values.dateTimeAttributePath)}
              emptyMessage={t('common:emptyCombobox')}
              disabled={!canEdit}
            />
            <Combobox
              control={control}
              name="assignedByAttributePath"
              label={t('integrations:bitrixTouch.fields.assignedByAttributePath')}
              options={withSavedPath(attributeOptions, values.assignedByAttributePath)}
              emptyMessage={t('common:emptyCombobox')}
              disabled={!canEdit}
            />
          </div>
          {canEdit && (
            <div className="flex justify-end">
              <Button type="submit" disabled={isPending}>
                {t('common:save')}
              </Button>
            </div>
          )}
        </form>
      </FormProvider>
    </AwaitContainer>
  );
};
